import { useState, useEffect, useCallback } from 'react';
import { cancellable } from 'services/utility/cancellable';

export enum AudioPlayerState {
    PLAYING, PAUSED,
}

export function useAudioPlayer(audioRef: React.RefObject<HTMLAudioElement>, stream: MediaStream | null, paused: boolean) {
    const [isMute, setMute] = useState(true);
    const [isPlaying, setPlaying] = useState(false);

    const startPlaying = useCallback((audio: HTMLAudioElement) => {
        function unmute() {
            audio.muted = false;
        }

        return cancellable(async isCanceled => {
            try {
                await audio.play();
                if (isCanceled()) return;
                unmute();
                setPlaying(true);
            } catch (err) {
                if (err instanceof Error) {
                    console.warn('cannot start playing: ', err.message);
                } else {
                    console.error(err);
                }
            }
        });
    }, [setPlaying]);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            if (audio.srcObject !== stream) {
                if (audio.srcObject) {
                    audio.pause();
                    audio.muted = true;
                    setPlaying(false);
                }

                audio.srcObject = stream;
            }

            if (audio.srcObject) {
                if (isMute) {
                    audio.pause();
                    setPlaying(false);
                } else {
                    return startPlaying(audio);
                }
            }
        }

        return () => { /* nothing to do */ };
    }, [audioRef, stream, isMute, startPlaying]);

    useEffect(() => {
        setMute(paused);
    }, [paused]);

    return {
        state: isPlaying ? AudioPlayerState.PLAYING : AudioPlayerState.PAUSED
    };
}
